import {
  BookOpenText,
  MessageCircle,
  FileText,
  Info,
  Languages,
  SwatchBook,
  Bot,
  Plug,
  ScrollText,
} from 'lucide-react';
import { Tab } from '@headlessui/react';
import { useTranslation } from 'react-i18next';
import { ReactNode } from 'react';
import { ConfigFile, ConfigGroupKey } from './types';
import { ConfigurationGroupTab } from './ConfigurationGroupTab';
import { ProjectPromptsTab } from './ProjectPromptsTab';
import { useOrgModules } from '@/hooks/useOrgModules';
import { useAuthStore } from '@/stores/authStore';

interface OntologyManagementTabsProps {
  selectedTab: number;
  onTabChange: (index: number) => void;
  groupedFiles: Record<ConfigGroupKey, ConfigFile[]>;
  expandedSpecs: Record<string, boolean>;
  recentlySaved: Record<string, boolean>;
  onToggleSpecs: (fileId: string) => void;
  onViewFile: (fileId: string) => void;
  onDownloadFile: (fileId: string) => void;
  onUploadClick: (fileId: string) => void;
  isStructuredEditor: (fileId: string) => boolean;
  providersTab: ReactNode;
  servicesTab: ReactNode;
  /** Mode switch + template actions, rendered in the Ontology tab toolbar. */
  ontologyToolbar: ReactNode;
  onOpenInformation: () => void;
}

const tabClassName = ({ selected }: { selected: boolean }) =>
  `flex-1 min-w-[140px] rounded-lg px-3 py-2.5 text-sm font-medium transition-colors flex items-center justify-center gap-2
  ${selected ? 'bg-background text-foreground shadow' : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground'}`;

/**
 * The five configuration groups, previously top-level tabs. Their former "How it works"
 * callouts are now the section descriptions, so each block explains itself in place.
 */
const ONTOLOGY_SECTIONS: Array<{
  key: ConfigGroupKey;
  icon: typeof BookOpenText;
  titleKey: string;
  defaultTitle: string;
  descriptionKey: string;
}> = [
  {
    key: 'ontologie',
    icon: BookOpenText,
    titleKey: 'knowledgeManagement.sections.ontologie',
    defaultTitle: 'Ontologie',
    descriptionKey: 'knowledgeManagement.ontologyInfo',
  },
  {
    key: 'themes',
    icon: SwatchBook,
    titleKey: 'knowledgeManagement.sections.themes',
    defaultTitle: 'Thèmes',
    descriptionKey: 'knowledgeManagement.themesInfo',
  },
  {
    key: 'thesaurus',
    icon: Languages,
    titleKey: 'knowledgeManagement.sections.thesaurus',
    defaultTitle: 'Thésaurus',
    descriptionKey: 'knowledgeManagement.thesaurusInfo',
  },
  {
    key: 'prompts',
    icon: MessageCircle,
    titleKey: 'knowledgeManagement.sections.prompts',
    defaultTitle: 'Prompts',
    descriptionKey: 'knowledgeManagement.promptsInfo',
  },
  {
    key: 'documents',
    icon: FileText,
    titleKey: 'knowledgeManagement.sections.documents',
    defaultTitle: 'Documents',
    descriptionKey: 'knowledgeManagement.documentsInfo',
  },
];

export function OntologyManagementTabs({
  selectedTab,
  onTabChange,
  groupedFiles,
  expandedSpecs,
  recentlySaved,
  onToggleSpecs,
  onViewFile,
  onDownloadFile,
  onUploadClick,
  isStructuredEditor,
  providersTab,
  servicesTab,
  ontologyToolbar,
  onOpenInformation,
}: OntologyManagementTabsProps) {
  const { t } = useTranslation();
  const { moduleEnabled } = useOrgModules();
  // Same organization scope as the « Services IA » tab: the user's own.
  const organizationId = useAuthStore((s) => s.user?.organization_id ?? null);
  const showProjectPrompts = moduleEnabled('projects');

  return (
    <Tab.Group selectedIndex={selectedTab} onChange={onTabChange}>
      <Tab.List className="flex flex-wrap gap-2 rounded-xl bg-muted p-1 mb-6">
        <Tab className={tabClassName}>
          <Plug className="h-4 w-4" />
          {t('knowledgeManagement.tabs.providers', 'Fournisseurs IA')}
        </Tab>
        <Tab className={tabClassName}>
          <Bot className="h-4 w-4" />
          {t('knowledgeManagement.tabs.services', 'Services IA')}
        </Tab>
        {showProjectPrompts && (
          <Tab className={tabClassName}>
            <ScrollText className="h-4 w-4" />
            {t('knowledgeManagement.tabs.projectPrompts', 'Prompts Projets')}
          </Tab>
        )}
        <Tab className={tabClassName}>
          <BookOpenText className="h-4 w-4" />
          {t('knowledgeManagement.tabs.ontology', 'Ontologie')}
        </Tab>
      </Tab.List>

      <Tab.Panels>
        <Tab.Panel>{providersTab}</Tab.Panel>
        <Tab.Panel>{servicesTab}</Tab.Panel>
        {showProjectPrompts && (
          <Tab.Panel>
            <ProjectPromptsTab organizationId={organizationId} />
          </Tab.Panel>
        )}
        <Tab.Panel>
          <div className="space-y-8">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="flex flex-wrap items-center gap-2">{ontologyToolbar}</div>
              <button
                type="button"
                onClick={onOpenInformation}
                aria-label={t('knowledgeManagement.informationAria', 'Pourquoi configurer votre assistant ?')}
                title={t('knowledgeManagement.informationAria', 'Pourquoi configurer votre assistant ?')}
                className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full border border-border text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <Info className="h-4 w-4" />
              </button>
            </div>

            <p className="text-sm text-muted-foreground">{t('knowledgeManagement.modeHint')}</p>

            {ONTOLOGY_SECTIONS.map(({ key, icon: Icon, titleKey, defaultTitle, descriptionKey }) => {
              const files = groupedFiles[key];
              if (files.length === 0) return null;

              return (
                <section key={key} className="space-y-4">
                  <div className="border-b border-border pb-3">
                    <h2 className="flex items-center gap-2 text-lg font-semibold text-foreground">
                      <Icon className="h-5 w-5 text-primary" />
                      {t(titleKey, defaultTitle)}
                    </h2>
                    <p className="mt-1.5 max-w-4xl text-sm leading-relaxed text-muted-foreground">
                      {t(descriptionKey)}
                    </p>
                  </div>

                  <ConfigurationGroupTab
                    files={files}
                    expandedSpecs={expandedSpecs}
                    recentlySaved={recentlySaved}
                    onToggleSpecs={onToggleSpecs}
                    onViewFile={onViewFile}
                    onDownloadFile={onDownloadFile}
                    onUploadClick={onUploadClick}
                    isStructuredEditor={isStructuredEditor}
                  />
                </section>
              );
            })}
          </div>
        </Tab.Panel>
      </Tab.Panels>
    </Tab.Group>
  );
}
